import React from "react";
import { useTranslate } from "@refinedev/core";
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import type { UnifiedEvent } from "../../../types/events";
import { UnifiedEventCard } from "./UnifiedEventCard";

interface EventCardStripProps {
  events: UnifiedEvent[];
  focusedEventKey: string | null;
  onFocusToggle: (key: string) => void;
  onNavigate?: (event: UnifiedEvent) => void;
  onHoverEvent?: (key: string | null) => void;
}

export const EventCardStrip: React.FC<EventCardStripProps> = ({
  events,
  focusedEventKey,
  onFocusToggle,
  onNavigate,
  onHoverEvent,
}) => {
  const translate = useTranslate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const isXLScreen = useMediaQuery(theme.breakpoints.up("xl"));

  const cardWidth = isSmallScreen
    ? "calc((100% - 1rem) / 1.5)"
    : isMediumScreen
      ? "calc((100% - 2rem) / 2.5)"
      : isXLScreen
        ? "calc((100% - 4rem) / 5.5)"
        : "calc((100% - 3rem) / 4.5)";

  if (events.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: { xs: "120px", md: "140px" },
          px: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {translate("monitoring.noActiveEvents", "No active events")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        gap: { xs: 1, md: 2 },
        overflowX: "auto",
        pb: 1,
        px: { xs: 1, md: 2 },
        minHeight: { xs: "120px", md: "140px" },
        "&::-webkit-scrollbar": { height: { xs: "6px", md: "8px" } },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "rgba(0,0,0,0.2)",
          borderRadius: "4px",
        },
      }}
    >
      {events.map((event) => (
        <UnifiedEventCard
          key={event.key}
          event={event}
          isFocused={focusedEventKey === event.key}
          isAnyFocused={focusedEventKey !== null}
          onFocusToggle={() => onFocusToggle(event.key)}
          onNavigate={onNavigate ? () => onNavigate(event) : undefined}
          onHoverStart={() => onHoverEvent?.(event.key)}
          onHoverEnd={() => onHoverEvent?.(null)}
          cardWidth={cardWidth}
        />
      ))}
    </Box>
  );
};
